import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";

const GSI_SRC = "https://accounts.google.com/gsi/client";
let scriptPromise = null;

const loadGsi = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = GSI_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => { scriptPromise = null; reject(new Error("Google sign-in could not be loaded")); };
    document.head.appendChild(el);
  });
  return scriptPromise;
};

/**
 * Real Google Identity Services button. The signed credential is verified on our server, which then
 * applies the invitation rules and the two-step requirement — Google only ever proves identity.
 * The client id and a single-use nonce come from the backend, so rotating them needs no rebuild.
 * Hidden entirely when no client id is configured.
 */
export const GoogleSignIn = ({ onSession, onMfa, onError, inviteToken = "", agreeTerms = false,
                               disabled = false, text = "continue_with", mode = "signin",
                               testid = "google-signin-btn" }) => {
  const host = useRef(null);
  const [config, setConfig] = useState(null);
  const [state, setState] = useState("loading");
  const [message, setMessage] = useState("");

  const fail = useCallback((text2) => {
    setMessage(text2);
    onError?.(text2);
  }, [onError]);

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/google/config")
      .then(({ data }) => { if (!cancelled) { setConfig(data); if (!data.enabled) setState("off"); } })
      .catch(() => { if (!cancelled) setState("off"); });
    return () => { cancelled = true; };
  }, []);

  const submit = useCallback(async (credential) => {
    setState("verifying");
    setMessage("");
    try {
      const { data } = await api.post(mode === "link" ? "/auth/google/link" : "/auth/google/verify", {
        credential, invite_token: inviteToken, agree_terms: agreeTerms });
      if (data.mfa_required) onMfa?.(data);
      else onSession?.(data);
    } catch (e) {
      setState("ready");
      fail(apiError(e.response?.data?.detail));
    }
  }, [mode, inviteToken, agreeTerms, onMfa, onSession, fail]);

  useEffect(() => {
    if (!config?.enabled || !host.current) return undefined;
    let cancelled = false;
    loadGsi()
      .then(() => {
        if (cancelled || !host.current) return;
        window.google.accounts.id.initialize({
          client_id: config.client_id,
          nonce: config.nonce,
          callback: (res) => submit(res.credential),
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        host.current.innerHTML = "";
        window.google.accounts.id.renderButton(host.current, {
          theme: "outline", size: "large", shape: "pill", text,
          logo_alignment: "center", width: 320,
        });
        setState("ready");
      })
      .catch(() => { if (!cancelled) setState("off"); });
    return () => { cancelled = true; };
  }, [config, submit, text]);

  if (state === "off") return null;

  return (
    <div data-testid={testid}>
      <div ref={host}
           className={`flex justify-center ${state !== "ready" || disabled ? "pointer-events-none opacity-50" : ""}`} />
      {state === "loading" && (
        <p className="text-sm text-muted-foreground text-center" data-testid="google-loading">
          Loading Google sign-in…
        </p>
      )}
      {state === "verifying" && (
        <p className="mt-2 text-sm font-semibold text-center text-muted-foreground" data-testid="google-verifying">
          Checking your Google account…
        </p>
      )}
      {disabled && state === "ready" && (
        <p className="mt-2 text-xs font-semibold text-center text-muted-foreground" data-testid="google-blocked-note">
          Please accept the daycare terms above first.
        </p>
      )}
      {message && (
        <p className="mt-3 text-sm font-semibold text-[#EF4444] bg-red-50 rounded-2xl p-4" data-testid="google-error">
          {message}
        </p>
      )}
    </div>
  );
};
